import { useMemo, useState } from "react";
import { Copy, DoorClosed, DoorOpen, Pencil, Share2, ShieldCheck, Undo2 } from "lucide-react";
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle } from "./ui";
import { DeadlineField, deadlineText, useDeadlineInput } from "./Deadline";
import { PinSettings } from "./AdminAccess";
import { toast } from "sonner";
import { api, apiError } from "../../lib/api";
import { adminHeaders, adminLink, joinLink, lmsStore, type LmsComp } from "../../lib/lms";
import { PopButton } from "../PopButton";
import { TeamBadge } from "./TeamBadge";

const box = "rounded-[22px] border-[3px] border-[#0B1F33] bg-[#0B1F33] p-3 text-white shadow-[0_4px_0_#000] sm:p-4";

async function copy(text: string, label: string) {
  try { await navigator.clipboard.writeText(text); toast.success(`${label} copied`); } catch { toast.error("Couldn't copy — long-press to copy instead."); }
}

function Invite({ comp }: { comp: LmsComp }) {
  const link = joinLink(comp.id);
  const share = () => (navigator.share ? navigator.share({ title: comp.name, text: `Join my Last Man Standing: ${comp.name}`, url: link }).catch(() => undefined) : copy(link, "Join link"));
  const token = lmsStore.admin(comp.id) || "";
  return (
    <div className="space-y-1.5" data-testid="lms-invite">
      <p className="text-xs font-extrabold uppercase tracking-wide text-[#FFE14D]">Invite players ({comp.players.length}/{comp.max_players})</p>
      <div className="flex gap-1.5">
        <input readOnly value={link} className="field !py-2 !text-xs !text-[#0B1F33]" data-testid="lms-join-link" onFocus={(e) => e.target.select()} />
        <PopButton size="sm" tone="yellow" onClick={() => copy(link, "Join link")} aria-label="Copy join link" data-testid="lms-copy-join-link"><Copy className="h-4 w-4" /></PopButton>
        <PopButton size="sm" tone="green" onClick={share} aria-label="Share join link" data-testid="lms-share-join-link"><Share2 className="h-4 w-4" /></PopButton>
      </div>
      <button className="text-[11px] font-bold text-white/60 underline" onClick={() => copy(adminLink(comp.id, token), "Admin link")} data-testid="lms-copy-admin-link">Copy my private admin link (keep it secret — use it on another device)</button>
    </div>
  );
}

function Results({ comp, onChange }: { comp: LmsComp; onChange: (c: LmsComp) => void }) {
  const teams = useMemo(() => [...new Set(comp.players.filter((p) => p.alive && p.pick).map((p) => p.pick as string))].sort(), [comp.players]);
  const [res, setRes] = useState<Record<string, "W" | "D" | "L">>({});
  const [busy, setBusy] = useState(false);
  const noPick = comp.players.filter((p) => p.alive && !p.pick).length;
  const submit = async () => {
    setBusy(true);
    try {
      const { data } = await api.post(`/lms/${comp.id}/results`, { results: res }, adminHeaders(comp.id));
      onChange(data);
      setRes({});
      toast.success(`GW ${comp.gameweek} results saved`);
    } catch (e) { toast.error(apiError(e)); } finally { setBusy(false); }
  };
  return (
    <div className="space-y-2" data-testid="lms-results-entry">
      <p className="text-xs font-extrabold uppercase tracking-wide text-[#FFE14D]">Enter GW {comp.gameweek} results</p>
      {noPick > 0 && <p className="text-xs font-bold text-[#FF9C9C]" data-testid="lms-no-pick-warning">{noPick} player{noPick > 1 ? "s" : ""} didn't pick and will be eliminated.</p>}
      {teams.map((t) => (
        <div key={t} className="flex items-center gap-2 rounded-xl bg-white/10 px-2 py-1.5">
          <TeamBadge team={t} size="sm" /><span className="min-w-0 flex-1 truncate text-sm font-bold">{t}</span>
          {(["W", "D", "L"] as const).map((r) => (
            <button key={r} onClick={() => setRes({ ...res, [t]: r })} data-testid={`lms-result-${t.toLowerCase().replace(/[^a-z]+/g, "-")}-${r}`}
              className={`h-8 w-9 rounded-lg border-2 font-comic text-sm transition-transform active:scale-95 ${res[t] === r ? (r === "W" ? "border-white bg-[#35E07A] text-[#0B1F33]" : r === "D" ? "border-white bg-[#FFE14D] text-[#0B1F33]" : "border-white bg-[#FF5D5D]") : "border-white/30 bg-transparent text-white/80"}`}>{r}</button>
          ))}
        </div>
      ))}
      {!teams.length && <p className="text-sm font-bold text-white/70">Nobody picked a team this week.</p>}
      <PopButton tone="yellow" className="w-full" disabled={busy || teams.some((t) => !res[t])} onClick={submit} data-testid="lms-submit-results">Confirm results & eliminate</PopButton>
    </div>
  );
}

function TeamsEditor({ comp, onChange }: { comp: LmsComp; onChange: (c: LmsComp) => void }) {
  const [open, setOpen] = useState(false);
  const [text, setText] = useState(comp.teams.join("\n"));
  const save = async () => {
    try {
      const { data } = await api.put(`/lms/${comp.id}/teams`, { teams: text.split("\n") }, adminHeaders(comp.id));
      onChange(data); setOpen(false); toast.success("Team list updated");
    } catch (e) { toast.error(apiError(e)); }
  };
  if (!open) return <button className="flex items-center gap-1 text-xs font-bold text-white/70 underline" onClick={() => setOpen(true)} data-testid="lms-edit-teams"><Pencil className="h-3.5 w-3.5" /> Edit team list ({comp.teams.length} teams)</button>;
  return (
    <div className="space-y-1.5">
      <textarea className="field h-48 !text-xs !text-[#0B1F33]" value={text} onChange={(e) => setText(e.target.value)} data-testid="lms-teams-textarea" />
      <div className="flex gap-1.5"><PopButton size="sm" tone="yellow" onClick={save} data-testid="lms-save-teams">Save teams</PopButton><PopButton size="sm" tone="white" onClick={() => setOpen(false)}>Cancel</PopButton></div>
    </div>
  );
}

function UndoButton({ comp, onChange }: { comp: LmsComp; onChange: (c: LmsComp) => void }) {
  const [open, setOpen] = useState(false);
  const gw = comp.history[comp.history.length - 1].number;
  const undo = async () => {
    try {
      const { data } = await api.post(`/lms/${comp.id}/undo`, {}, adminHeaders(comp.id));
      onChange(data);
      toast.success(`GW ${gw} results undone — re-enter them below`);
    } catch (e) { toast.error(apiError(e)); }
  };
  return (
    <>
      <button className="flex items-center gap-1 text-xs font-bold text-[#FF9C9C] underline" onClick={() => setOpen(true)} data-testid="lms-undo-results"><Undo2 className="h-3.5 w-3.5" /> Undo GW {gw} results</button>
      <AlertDialog open={open} onOpenChange={setOpen}>
        <AlertDialogContent className="border-[3px] border-[#0B1F33]" data-testid="lms-undo-dialog">
          <AlertDialogHeader>
            <AlertDialogTitle className="font-comic text-2xl">Undo GW {gw} results?</AlertDialogTitle>
            <AlertDialogDescription>Players knocked out in GW {gw} come back, any winner is cleared, and picks stay locked so you can enter the correct results.</AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel data-testid="lms-undo-cancel">Cancel</AlertDialogCancel>
            <AlertDialogAction onClick={undo} data-testid="lms-undo-confirm">Undo results</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </>
  );
}

export function AdminPanel({ comp, onChange }: { comp: LmsComp; onChange: (c: LmsComp) => void }) {
  const [busy, setBusy] = useState(false);
  const dl = useDeadlineInput();
  const setWindow = async (action: "open" | "close" | "deadline", clear = false) => {
    setBusy(true);
    try {
      const { data } = await api.post(`/lms/${comp.id}/window`, { action, deadline_uk: clear ? null : dl.value }, adminHeaders(comp.id));
      onChange(data);
      toast.success(action === "open" ? `GW ${comp.gameweek} picks are open` : action === "deadline" ? (data.deadline ? "Deadline updated" : "Deadline removed") : "Picks locked — enter the results when the games are done");
    } catch (e) { toast.error(apiError(e)); } finally { setBusy(false); }
  };
  const picked = comp.players.filter((p) => p.alive && p.picked).length;
  const alive = comp.players.filter((p) => p.alive).length;
  const teamsLocked = comp.history.length > 0 || comp.window !== "pending" || comp.players.some((p) => p.picked);
  return (
    <section className={box} data-testid="lms-admin-panel">
      <h2 className="flex items-center gap-2 font-comic text-xl text-[#35E07A]"><ShieldCheck className="h-5 w-5" strokeWidth={3} /> Admin controls</h2>
      <div className="mt-2 grid gap-3 md:grid-cols-2">
        <div className="space-y-2">
          {comp.status === "active" && comp.window === "pending" && <DeadlineField s={dl} />}
          {comp.status === "active" && comp.window === "pending" && (
            <PopButton tone="green" className="w-full" disabled={busy} onClick={() => setWindow("open")} data-testid="lms-open-window"><DoorOpen className="h-5 w-5" /> Open GW {comp.gameweek} picks</PopButton>
          )}
          {comp.window === "open" && (
            <>
              <p className="text-sm font-bold" data-testid="lms-picked-count">{picked} of {alive} players have picked</p>
              <p className="text-xs font-bold text-white/70" data-testid="lms-admin-deadline">{comp.deadline ? `Auto-closes ${deadlineText(comp.deadline)}` : "No deadline — close picks manually."}</p>
              <DeadlineField s={dl} />
              <div className="flex gap-1.5">
                <PopButton size="sm" tone="yellow" disabled={busy || !dl.value} onClick={() => setWindow("deadline")} data-testid="lms-save-deadline">{comp.deadline ? "Change deadline" : "Set deadline"}</PopButton>
                {comp.deadline && <PopButton size="sm" tone="white" disabled={busy} onClick={() => setWindow("deadline", true)} data-testid="lms-clear-deadline">Remove deadline</PopButton>}
              </div>
              <PopButton tone="orange" className="w-full" disabled={busy} onClick={() => setWindow("close")} data-testid="lms-close-window"><DoorClosed className="h-5 w-5" /> Close picks</PopButton>
            </>
          )}
          {comp.status === "active" && comp.window === "closed" && <Results comp={comp} onChange={onChange} />}
          {comp.status === "finished" && <p className="text-sm font-bold">Competition finished — see the winner above.</p>}
          {!teamsLocked && <TeamsEditor comp={comp} onChange={onChange} />}
          <PinSettings key={String(comp.has_pin)} comp={comp} onChange={onChange} />
          {comp.history.length > 0 && comp.window === "pending" && <UndoButton comp={comp} onChange={onChange} />}
        </div>
        <Invite comp={comp} />
      </div>
    </section>
  );
}
